
import { supabase } from '@/integrations/supabase/client';

export interface InternalProfileData {
  user_id: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  bio?: string;
  profile_photo_url?: string;
  position?: string;
  jersey_number?: number;
  years_playing?: number;
  skill_level?: string;
  achievements?: string;
  height?: string;
  weight?: string;
  fitness_level?: string;
  grade_level?: string;
  current_sport?: string;
  season_status?: string;
  highlight_video_links?: string[];
  photo_gallery_links?: string[];
  show_profile_public?: boolean;
  show_contact_public?: boolean;
  [key: string]: any; // Allow additional fields
}

export const internalProfileService = {
  async upsertProfile(profileData: InternalProfileData) {
    try {
      console.log('=== INTERNAL PROFILE SERVICE UPSERT START ===');
      console.log('Profile data to save:', JSON.stringify(profileData, null, 2));

      // Clean up the data - ensure arrays are proper arrays
      const cleanedData = {
        ...profileData,
        preferred_sports: Array.isArray(profileData.preferred_sports) ? profileData.preferred_sports : [],
        facilities_owned: Array.isArray(profileData.facilities_owned) ? profileData.facilities_owned : [],
        connected_teams: Array.isArray(profileData.connected_teams) ? profileData.connected_teams : [],
      };

      console.log('Cleaned data for upsert:', JSON.stringify(cleanedData, null, 2));

      const { data, error } = await supabase
        .from('internal_user_profiles')
        .upsert(cleanedData, {
          onConflict: 'user_id'
        })
        .select()
        .single();

      if (error) {
        console.error('=== UPSERT ERROR ===');
        console.error('Error details:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        return { success: false, error: error.message };
      }

      console.log('=== UPSERT SUCCESS ===');
      console.log('Saved profile data:', JSON.stringify(data, null, 2));

      // Verify critical fields were saved
      if (profileData.profile_photo_url && data?.profile_photo_url !== profileData.profile_photo_url) {
        console.warn('Profile photo URL mismatch! Expected:', profileData.profile_photo_url, 'Got:', data?.profile_photo_url);
      }

      // If this is a coach profile with connections, sync them to user_connections table
      if (profileData.role === 'coach' && profileData.user_id) {
        await this.syncCoachConnections(profileData.user_id, profileData);
      }

      return { success: true, data };
    } catch (error) {
      console.error('=== INTERNAL PROFILE SERVICE CATCH ERROR ===');
      console.error('Exception:', error);
      return { success: false, error: 'Failed to save profile' };
    }
  },

  async syncCoachConnections(userId: string, profileData: any) {
    try {
      const { userConnectionsService } = await import('@/services/userConnectionsService');
      
      // Handle connected players
      if (Array.isArray(profileData.connected_players)) {
        for (const playerId of profileData.connected_players) {
          if (playerId && playerId !== userId && typeof playerId === 'string') {
            try {
              await userConnectionsService.createConnection(
                userId,
                playerId,
                'connect',
                'internal',
                'internal' // Assuming internal users for now
              );
            } catch (error) {
              console.log('Connection may already exist:', error);
            }
          }
        }
      }

      // Handle connected coaches
      if (Array.isArray(profileData.connected_coaches)) {
        for (const coachId of profileData.connected_coaches) {
          if (coachId && coachId !== userId && typeof coachId === 'string') {
            try {
              await userConnectionsService.createConnection(
                userId,
                coachId,
                'connect',
                'internal',
                'internal'
              );
            } catch (error) {
              console.log('Connection may already exist:', error);
            }
          }
        }
      }

      // Handle connected teams - these would need team connection service
      // We'll skip teams for now as they require different handling
      
    } catch (error) {
      console.error('Error syncing coach connections:', error);
    }
  },

  async getProfile(userId: string) {
    try {
      const { data, error } = await supabase
        .from('internal_user_profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching internal profile:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Internal profile service error:', error);
      return { success: false, error: 'Failed to fetch profile' };
    }
  },

  async updateProfilePhoto(userId: string, photoUrl: string) {
    try {
      console.log('Updating profile photo for user:', userId, 'with URL:', photoUrl);

      const { data, error } = await supabase
        .from('internal_user_profiles')
        .update({ profile_photo_url: photoUrl })
        .eq('user_id', userId)
        .select()
        .single();

      if (error) {
        console.error('Error updating profile photo:', error);
        return { success: false, error: error.message };
      }

      console.log('Profile photo updated successfully:', data);
      return { success: true, data };
    } catch (error) {
      console.error('Error updating profile photo:', error);
      return { success: false, error: 'Failed to update profile photo' };
    }
  }
};
